import type { QueryKey } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";

import { useApiQuery } from "@ja/portal-query";

/** Search option returned from the API */
export interface SearchOption {
    id: string | number;
    label: string;
}

/** API response format */
interface SearchOptionsResponse {
    options: SearchOption[];
    error?: string;
}

/** Parameters for useModelSearchOptions composable */
export interface UseModelSearchOptionsParams {
    /** Model name for the search endpoint */
    model: MaybeRefOrGetter<string | undefined>;
    /** Field to use for display label */
    labelField: MaybeRefOrGetter<string>;
    /** Field(s) to search on */
    searchField: MaybeRefOrGetter<string>;
    /** Search query string */
    query: MaybeRefOrGetter<string>;
    /** Whether the query is enabled */
    enabled?: MaybeRefOrGetter<boolean>;
}

/** Parameters for fetching a single option by ID */
export interface UseModelSearchByIdParams {
    /** Model name for the search endpoint */
    model: MaybeRefOrGetter<string | undefined>;
    /** Field to use for display label */
    labelField: MaybeRefOrGetter<string>;
    /** Record ID to fetch */
    id: MaybeRefOrGetter<string | number | null | undefined>;
    /** Whether the query is enabled */
    enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Builds the query key for search options.
 * Using a consistent key structure enables proper caching and deduplication.
 */
function buildQueryKey(
    model: string,
    label: string,
    search: string,
    query: string
): QueryKey {
    return ["crud", "search-options", model, label, search, query];
}

/**
 * Builds the query key for fetching by ID.
 */
function buildIdQueryKey(
    model: string,
    label: string,
    id: string | number
): QueryKey {
    return ["crud", "search-options", model, label, "by-id", id];
}

/**
 * Composable for fetching model search options with TanStack Query.
 *
 * Provides automatic caching, deduplication, and stale-while-revalidate behavior.
 * Query key structure: ['crud', 'search-options', model, label, search, query]
 *
 * @example
 * ```typescript
 * const searchQuery = refDebounced(ref(''), 300)
 *
 * const { options, isLoading, error } = useModelSearchOptions({
 *   model: 'users',
 *   labelField: 'full_name',
 *   searchField: 'full_name',
 *   query: searchQuery,
 * })
 * ```
 */
export function useModelSearchOptions(params: UseModelSearchOptionsParams) {
    const { model, labelField, searchField, query, enabled = true } = params;

    const queryKey = computed(() => {
        const m = toValue(model);
        const l = toValue(labelField);
        const s = toValue(searchField);
        const q = toValue(query);

        if (!m) return ["crud", "search-options", "disabled"];
        return buildQueryKey(m, l, s, q);
    });

    const isEnabled = computed(() => {
        return !!toValue(model) && toValue(enabled);
    });

    const result = useApiQuery<SearchOptionsResponse>(
        queryKey,
        () => {
            const m = toValue(model);
            const l = toValue(labelField);
            const s = toValue(searchField);
            const q = toValue(query);
            const params = new URLSearchParams({ q, label: l, search: s });
            return `/crud/${m}/search-options?${params}`;
        },
        {},
        {
            enabled: isEnabled,
            // Keep data fresh for 5 minutes
            staleTime: 5 * 60 * 1000,
            // Cache for 30 minutes
            gcTime: 30 * 60 * 1000,
            // Keep previous data while fetching new results
            placeholderData: (previousData) => previousData
        }
    );

    return {
        /** Search options from the API */
        options: computed(() => result.data.value?.options ?? []),
        /** Whether the query is currently loading */
        isLoading: result.isLoading,
        /** Whether the query is fetching (includes background refetches) */
        isFetching: result.isFetching,
        /** Error from the query */
        error: computed(() =>
            result.error.value ? result.error.value.message : null
        ),
        /** Refetch the options */
        refetch: result.refetch
    };
}

/**
 * Composable for fetching a single option by ID.
 *
 * Used to display the label for an existing value.
 *
 * @example
 * ```typescript
 * const { option, isLoading } = useModelSearchById({
 *   model: 'users',
 *   labelField: 'full_name',
 *   id: props.modelValue,
 * })
 * ```
 */
export function useModelSearchById(params: UseModelSearchByIdParams) {
    const { model, labelField, id, enabled = true } = params;

    const queryKey = computed(() => {
        const m = toValue(model);
        const l = toValue(labelField);
        const i = toValue(id);

        if (!m || !i) return ["crud", "search-options", "by-id", "disabled"];
        return buildIdQueryKey(m, l, i);
    });

    const isEnabled = computed(() => {
        const m = toValue(model);
        const i = toValue(id);
        return !!m && i != null && i !== "" && toValue(enabled);
    });

    const result = useApiQuery<SearchOptionsResponse>(
        queryKey,
        () => {
            const m = toValue(model);
            const l = toValue(labelField);
            const i = toValue(id);
            const params = new URLSearchParams({ id: String(i), label: l });
            return `/crud/${m}/search-options?${params}`;
        },
        {},
        {
            enabled: isEnabled,
            // Labels rarely change, cache longer
            staleTime: 10 * 60 * 1000,
            gcTime: 60 * 60 * 1000
        }
    );

    return {
        /** The fetched option (id + label) */
        option: computed(() => result.data.value?.options?.[0] ?? null),
        /** Whether the query is loading */
        isLoading: result.isLoading,
        /** Error from the query */
        error: computed(() =>
            result.error.value ? result.error.value.message : null
        )
    };
}

/** Parameters for fetching multiple options by IDs (multi-select label resolution) */
export interface UseModelSearchByIdsParams {
    /** Model name for the search endpoint */
    model: MaybeRefOrGetter<string | undefined>;
    /** Field to use for display label */
    labelField: MaybeRefOrGetter<string>;
    /** Record IDs to fetch */
    ids: MaybeRefOrGetter<Array<string | number>>;
    /** Whether the query is enabled */
    enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Fetch labels for a known set of IDs in one round-trip.
 *
 * Used by multi-select pickers to render chips with proper labels for the
 * currently selected ids — fanning out to one request per id would be wasteful.
 */
export function useModelSearchByIds(params: UseModelSearchByIdsParams) {
    const { model, labelField, ids, enabled = true } = params;

    const sortedIds = computed(() => {
        const arr = (toValue(ids) ?? []).map((v) => String(v));
        // Sort so the cache key is stable regardless of selection order.
        return [...arr].sort();
    });

    const queryKey = computed<QueryKey>(() => {
        const m = toValue(model);
        const l = toValue(labelField);
        const idsKey = sortedIds.value;

        if (!m || idsKey.length === 0) return ["crud", "search-options", "by-ids", "disabled"];
        return ["crud", "search-options", m, l, "by-ids", idsKey.join(",")];
    });

    const isEnabled = computed(() => {
        return !!toValue(model) && sortedIds.value.length > 0 && toValue(enabled);
    });

    const result = useApiQuery<SearchOptionsResponse>(
        queryKey,
        () => {
            const m = toValue(model);
            const l = toValue(labelField);
            const params = new URLSearchParams();
            params.set("label", l);
            for (const i of sortedIds.value) {
                params.append("ids[]", i);
            }
            return `/crud/${m}/search-options?${params.toString()}`;
        },
        {},
        {
            enabled: isEnabled,
            staleTime: 10 * 60 * 1000,
            gcTime: 60 * 60 * 1000
        }
    );

    return {
        /** Fetched options keyed by id for quick lookup */
        options: computed(() => result.data.value?.options ?? []),
        /** Whether the query is loading */
        isLoading: result.isLoading,
        /** Error from the query */
        error: computed(() =>
            result.error.value ? result.error.value.message : null
        )
    };
}
